import { useState, useEffect, useCallback } from 'react'
import { ChevronDown, Map, RefreshCw } from 'lucide-react'
import leiloesService from '../../services/leiloesService'
import mapasService from '../../services/mapasService'
import { useOrganizador } from '../../contexts/OrganizadorContext'
import MapaVendedorPreview from './MapaVendedorPreview'
import MapaVendedorV2Preview from './MapaVendedorV2Preview'

// ─── Toggle switch ────────────────────────────────────────────────────────────
function Toggle({ checked, onChange, label }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer select-none">
      <div
        onClick={() => onChange(!checked)}
        className={`relative w-9 h-5 rounded-full transition-colors duration-200 ${checked ? 'bg-blue-600' : 'bg-gray-300'}`}
      >
        <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${checked ? 'translate-x-4' : 'translate-x-0'}`} />
      </div>
      {label && <span className="text-sm text-gray-700">{label}</span>}
    </label>
  )
}

// ─── Select estilizado ────────────────────────────────────────────────────────
function Select({ label, value, onChange, children, disabled }) {
  return (
    <div className="flex flex-col gap-1 w-full">
      {label && <label className="text-xs text-gray-500 font-medium">{label}</label>}
      <div className="relative">
        <select
          value={value}
          onChange={e => onChange(e.target.value)}
          disabled={disabled}
          className="w-full appearance-none border border-gray-300 rounded-lg px-3 py-2.5 pr-8 text-sm bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
        >
          {children}
        </select>
        <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
      </div>
    </div>
  )
}

export default function MapaVendedor() {
  const { organizadorAtivo } = useOrganizador()
  const orgId = organizadorAtivo?.id

  const [leiloes,     setLeiloes]     = useState([])
  const [vendedores,  setVendedores]  = useState([])
  const [leilaoId,    setLeilaoId]    = useState('')
  const [vendedorId,  setVendedorId]  = useState('')
  const [versao,      setVersao]      = useState('1')
  const [colunasV1, setColunasV1] = useState(['lote', 'comprador', 'qtd', 'comissao', 'despesas', 'valor_unitario', 'total', 'liquido'])
  const [colunasV2, setColunasV2] = useState(['lote', 'curral', 'comprador', 'situacao', 'qtd', 'descricao', 'valor_unitario', 'total', 'liquido', 'comissao'])
  const [showDbVendedor, setShowDbVendedor] = useState(false)
  const [showDbOrganizador, setShowDbOrganizador] = useState(true)
  const [loadingLeil, setLoadingLeil] = useState(false)
  const [loadingVend, setLoadingVend] = useState(false)
  const [loadingPrev, setLoadingPrev] = useState(false)
  const [dados,       setDados]       = useState(null)
  const [erro,        setErro]        = useState(null)

  // ── Carrega config global + leilões ──────────────────────────────────────
  useEffect(() => {
    if (!orgId) return
    setLoadingLeil(true)
    Promise.all([
      leiloesService.getByOrganizador(orgId, { status: 'on' }),
      mapasService.getConfig(orgId).catch(() => null),
    ]).then(([list, cfg]) => {
      const sorted = [...list].sort((a, b) => new Date(b.data_leilao) - new Date(a.data_leilao))
      setLeiloes(sorted)
      if (sorted.length > 0) setLeilaoId(String((sorted.find(l => l.status === 'on') || sorted[0]).id))
      if (cfg) {
        setVersao(cfg.mapa_vendedor_versao_padrao || '1')
        if (cfg.mapa_vendedor_v1_colunas_ativas) setColunasV1(cfg.mapa_vendedor_v1_colunas_ativas)
        if (cfg.mapa_vendedor_v2_colunas_ativas) setColunasV2(cfg.mapa_vendedor_v2_colunas_ativas)
        setShowDbVendedor(!!cfg.mapa_vendedor_dados_bancarios_vendedor)
        setShowDbOrganizador(cfg.mapa_vendedor_dados_bancarios_organizador !== false)
      }
    }).finally(() => setLoadingLeil(false))
  }, [orgId])

  // ── Carrega vendedores quando leilão muda ─────────────────────────────────
  useEffect(() => {
    if (!orgId || !leilaoId) { setVendedores([]); return }
    setLoadingVend(true)
    setDados(null)
    mapasService.getVendedores(orgId, leilaoId)
      .then(list => { setVendedores(list); setVendedorId('') })
      .catch(() => setVendedores([]))
      .finally(() => setLoadingVend(false))
  }, [orgId, leilaoId])

  // ── Busca dados do mapa ───────────────────────────────────────────────────
  const fetchDados = useCallback(async (vId) => {
    if (!orgId || !leilaoId) return
    setLoadingPrev(true)
    setDados(null)
    setErro(null)
    try {
      const result = await mapasService.getMapaVendedorDados(orgId, leilaoId, {
        vendedorId: vId || undefined,
      })
      setDados(result)
    } catch (err) {
      setErro(err.response?.data?.detail || err.message || 'Erro ao carregar dados')
    } finally {
      setLoadingPrev(false)
    }
  }, [orgId, leilaoId])

  // ── Auto-fetch ao mudar vendedor ──────────────────────────────────────────
  useEffect(() => {
    if (!orgId || !leilaoId || loadingVend) return
    fetchDados(vendedorId)
  }, [vendedorId, leilaoId, orgId, loadingVend]) // eslint-disable-line

  if (!orgId) return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-700">
      Selecione um organizador para gerar o Mapa do Vendedor.
    </div>
  )

  return (
    <div className="space-y-4">
      {/* Título */}
      <div className="flex items-center gap-1.5 flex-shrink-0">
        <Map className="w-5 h-5 text-cyan-600" />
        <h1 className="text-lg font-semibold text-gray-800">Mapa do Vendedor</h1>
        {loadingPrev && <RefreshCw className="w-4 h-4 animate-spin text-blue-500 ml-1" />}
      </div>

      {/* ── Filtros — linha única compacta ─────────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        <div className="flex flex-wrap items-end gap-3">

          {/* Leilão */}
          <div className="flex-[3] min-w-[200px]">
            <Select label="Leilão" value={leilaoId}
              onChange={v => { setLeilaoId(v); setDados(null) }}
              disabled={loadingLeil}>
              {loadingLeil
                ? <option>Carregando...</option>
                : leiloes.map(l => <option key={l.id} value={String(l.id)}>{l.nome}</option>)
              }
            </Select>
          </div>

          {/* Vendedor */}
          <div className="flex-[3] min-w-[200px]">
            <Select label="Vendedor" value={vendedorId}
              onChange={v => { setVendedorId(v); setDados(null) }}
              disabled={loadingVend || !leilaoId}>
              <option value="">TODOS OS VENDEDORES</option>
              {vendedores.map(v => (
                <option key={v.id} value={String(v.id)}>{v.nome}</option>
              ))}
            </Select>
          </div>

          {/* Versão */}
          <div className="flex-1 min-w-[100px]">
            <Select label="Versão" value={versao}
              onChange={v => { setVersao(v); setDados(null) }}>
              <option value="1">Versão 1</option>
              <option value="2">Versão 2</option>
            </Select>
          </div>

        </div>
      </div>

      {/* ── Erro ─────────────────────────────────────────────────────────── */}
      {erro && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">
          {erro}
        </div>
      )}

      {/* ── Preview com scroll horizontal no mobile ──────────────────────── */}
      {dados && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
          {versao === '2' ? (
            <MapaVendedorV2Preview
              dados={dados}
              colunasAtivas={colunasV2}
              orgId={orgId}
              leilaoId={leilaoId}
              vendedorId={vendedorId}
              isencaoDefesaAtiva={organizadorAtivo?.produtos_premium?.includes('isencao_defesa')}
              onReload={() => fetchDados(vendedorId)}
            />
          ) : (
            <MapaVendedorPreview
              dados={dados}
              colunasAtivas={colunasV1}
              showDbVendedor={showDbVendedor}
              showDbOrganizador={showDbOrganizador}
              orgId={orgId}
              leilaoId={leilaoId}
              vendedorId={vendedorId}
              isencaoDefesaAtiva={organizadorAtivo?.produtos_premium?.includes('isencao_defesa')}
              onReload={() => fetchDados(vendedorId)}
            />
          )}
          </div>
        </div>
      )}

      {/* Estado vazio */}
      {!dados && !loadingPrev && !erro && leilaoId && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8 text-center text-gray-400 text-sm">
          Selecione um vendedor acima para visualizar o mapa.
        </div>
      )}
    </div>
  )
}
